// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Streaming ZIP output.
 *
 * Two facts about fflate 0.8.3 shape everything here, and both were verified by building real
 * archives rather than read off a README:
 *
 * 1. IT NEVER WRITES ZIP64, AND IT DOES NOT TELL YOU. Above 4 GiB the central-directory offset in
 *    the end-of-central-directory record wraps modulo 2^32 and the archive is silently corrupt —
 *    a 4,429,192,286-byte archive recorded its directory at offset 134,221,348 instead of
 *    4,429,188,644, exactly 2^32 out. The entry count wraps the same way past 65,535. Info-ZIP and
 *    Python's zipfile both happen to recover such an archive, loudly and quietly respectively, but
 *    "most extractors can probably repair it" is not something to hand somebody with the only copy
 *    of their photographs in it. So the ceilings are CHECKED BEFORE the write starts and refused
 *    with an explanation.
 *
 * 2. STORED, NOT DEFLATED. Measured on camera-like JPEGs, deflate saved 0.04% of the bytes for 7.4
 *    times the CPU. Even across a mixed set with screenshots in it, 10% for 8x the time. Only
 *    `ZipPassThrough` actually records compression method 0 — `new ZipDeflate(name, { level: 0 })`
 *    still records method 8.
 *
 * fflate's `ondata` is synchronous and has no backpressure, so chunks are queued and drained at
 * the caller's await points. Verified to hold peak buffering at ~35 KB regardless of archive size.
 */

import { Zip, ZipPassThrough } from 'fflate';

/**
 * The hard ceiling, a little under 4 GiB.
 *
 * The margin covers the local headers, the central directory and the descriptors, none of which
 * are counted in the payload total — being conservative here costs a user with a 3.9 GB dataset
 * nothing, and being optimistic costs a user with a 4.01 GB dataset everything.
 */
export const ZIP_MAX_BYTES = 3_900_000_000;

/** The 16-bit entry count in the end-of-central-directory record. */
export const ZIP_MAX_ENTRIES = 65_000;

export interface ZipSink {
  write(chunk: Uint8Array): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
}

/**
 * Stream straight to a file the user chose. Chromium only.
 *
 * The awaited `write` IS the backpressure — it resolves when the bytes have been handed to the
 * filesystem, so the producer cannot outrun the disk.
 */
export function fileSystemSink(writable: FileSystemWritableFileStream): ZipSink {
  return {
    // The cast is TypeScript's SharedArrayBuffer-vs-ArrayBuffer split on typed arrays, not a real
    // ambiguity: fflate's output is always a plain Uint8Array and this page cannot be
    // cross-origin isolated, so a SharedArrayBuffer cannot exist here at all.
    write: (chunk) => writable.write(chunk as unknown as BufferSource),
    close: () => writable.close(),
    abort: async () => {
      try {
        await writable.close();
      } catch {
        /* the caller is already handling a failure; a close error here adds nothing */
      }
    },
  };
}

/**
 * Accumulate in memory, for the browsers with no save picker — every Firefox, every Safari.
 *
 * The cap is not arbitrary. Chromium documents its blob limit as 2 GB of memory on desktop, but
 * `total_physical_memory / 100` on Android — which on an 8 GB phone is about 80 MB. There is no
 * way to read the applicable limit at runtime, so the tool measures the export size up front,
 * tells the user which branch they are on, and refuses rather than dying at 96%.
 */
export function memorySink(limitBytes: number): ZipSink & { blob(): Blob } {
  const parts: BlobPart[] = [];
  let total = 0;
  return {
    async write(chunk) {
      total += chunk.byteLength;
      if (total > limitBytes) {
        throw new Error(
          `This browser cannot save a file this large without the File System Access API. ` +
            `The export passed ${(limitBytes / 1e9).toFixed(1)} GB. Use a Chromium browser, or export fewer images.`,
        );
      }
      // A copy, because fflate reuses its output buffer between callbacks.
      parts.push(new Uint8Array(chunk).buffer as ArrayBuffer);
    },
    async close() {},
    async abort() {
      parts.length = 0;
    },
    blob: () => new Blob(parts, { type: 'application/zip' }),
  };
}

export interface ZipEntry {
  readonly name: string;
  /** Either raw bytes, or a Blob streamed in slices so a 40 MB photograph is never fully resident. */
  readonly data: Uint8Array | Blob;
}

export interface ZipProgress {
  (entriesDone: number, entriesTotal: number, bytesWritten: number): void;
}

const SLICE = 4 * 1024 * 1024;

export class ZipTooBigError extends Error {}

/**
 * Write a ZIP to a sink, entry by entry.
 *
 * `entries` is an async iterable so the caller can produce entries lazily — the report CSV is
 * assembled from data already in memory, but the photographs are `File` handles that should only
 * be read when their turn comes.
 */
export async function writeZip(
  sink: ZipSink,
  entries: AsyncIterable<ZipEntry> | Iterable<ZipEntry>,
  opts: { readonly entryCount: number; readonly payloadBytes: number; readonly onProgress?: ZipProgress },
): Promise<void> {
  if (opts.payloadBytes > ZIP_MAX_BYTES) {
    throw new ZipTooBigError(
      `That export would be ${(opts.payloadBytes / 1e9).toFixed(2)} GB. The ZIP writer here cannot ` +
        `produce a valid archive above ${(ZIP_MAX_BYTES / 1e9).toFixed(1)} GB — it does not support ZIP64, and ` +
        `it would silently write a corrupt one rather than fail. Export in batches instead.`,
    );
  }
  if (opts.entryCount > ZIP_MAX_ENTRIES) {
    throw new ZipTooBigError(
      `That export would hold ${opts.entryCount.toLocaleString('en-GB')} files. The archive format used ` +
        `here stores the file count in 16 bits, so above ${ZIP_MAX_ENTRIES.toLocaleString('en-GB')} it wraps around ` +
        `and the archive is silently wrong. Export in batches instead.`,
    );
  }

  const queue: Uint8Array[] = [];
  let failure: Error | null = null;
  let finished = false;
  let written = 0;

  const zip = new Zip((err, chunk, final) => {
    if (err) {
      failure = err instanceof Error ? err : new Error(String(err));
      return;
    }
    if (chunk?.length) queue.push(chunk);
    if (final) finished = true;
  });

  const drain = async (): Promise<void> => {
    while (queue.length > 0) {
      const chunk = queue.shift()!;
      written += chunk.byteLength;
      await sink.write(chunk);
    }
    if (failure) throw failure;
  };

  try {
    let done = 0;
    for await (const entry of entries) {
      const file = new ZipPassThrough(entry.name);
      zip.add(file);
      if (entry.data instanceof Blob) {
        const size = entry.data.size;
        for (let off = 0; off < size; off += SLICE) {
          const end = Math.min(off + SLICE, size);
          const slice = new Uint8Array(await entry.data.slice(off, end).arrayBuffer());
          file.push(slice, end >= size);
          await drain();
        }
        // A zero-byte file never enters the loop above, so it still needs its terminator.
        if (size === 0) file.push(new Uint8Array(0), true);
      } else {
        file.push(entry.data, true);
      }
      await drain();
      done++;
      opts.onProgress?.(done, opts.entryCount, written);
    }
    zip.end();
    await drain();
    if (!finished) throw new Error('The archive did not finish cleanly.');
    await sink.close();
  } catch (err) {
    await sink.abort();
    throw err;
  }
}
